import React, { useState, memo } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';
import KanbanCard from './KanbanCard';
import KanbanCardSkeleton from './KanbanCardSkeleton';
import KanbanEmptyColumn from './KanbanEmptyColumn';

/**
 * Estilo do wrapper do Draggable — REGRA CRÍTICA do @hello-pangea/dnd:
 * NUNCA sobrescrever/zerar a `transition` do draggableProps.style. A lib
 * detecta o FIM da drop animation pelo evento `transitionend`; com
 * `transition: none` o evento nunca dispara e o onDragEnd (que move o card
 * de coluna via update otimista) fica PRESO por segundos, até um
 * transitionend aleatório de um filho borbulhar — era a causa do card
 * demorar 3-5s para assentar na coluna destino.
 *
 * Padrão documentado da lib para drop INSTANTÂNEO: encurtar a duração
 * (0.001s) durante isDropAnimating — o transitionend ainda dispara.
 */
const getDragWrapperStyle = (style, snapshot) => {
  if (snapshot.isDropAnimating) {
    return { ...style, transitionDuration: '0.001s' };
  }
  return style;
};

/**
 * KanbanColumn — A single Kanban column with droppable area
 *
 * memo(): o Droppable/Draggable recebe atualizações via contexto do
 * DragDropContext (não via props), então memoizar a coluna é seguro e
 * recomendado pela doc do dnd — evita re-renderizar as 4 colunas inteiras
 * (todos os cards) quando o board re-renderiza no início/fim do arrasto.
 *
 * Props:
 *   column: { id, title, icon, color, bgColor, description }
 *   videos: array
 *   count: number
 *   isLoading: boolean
 *   onOpenScript: fn
 *   onCardClick: fn
 *   isMobile: boolean
 */
const KanbanColumn = memo(({ column, videos, count, isLoading, onChecklistChange, onOpenScript, onCardClick, isMobile }) => {
  const { t } = useTranslation();
  // Lazy loading for PUBLICADO column
  const [visibleCount, setVisibleCount] = useState(10);

  const isPostado = column.id === 'postado';
  const visibleVideos = isPostado
    ? videos.slice(0, visibleCount)
    : videos;
  const hasMore = isPostado && visibleCount < videos.length;

  const handleLoadMore = () => setVisibleCount((prev) => prev + 10);

  return (
    <div
      style={{
        flex: isMobile ? '1 1 auto' : 1,
        minWidth: isMobile ? undefined : 0,
        height: isMobile ? 'auto' : '100%',
        minHeight: isMobile ? '300px' : undefined,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--dp-bg-card, rgba(30,30,30,0.5))',
        border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.06))',
        borderRadius: '12px',
        position: 'relative',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Top color bar */}
      <div
        style={{
          height: '3px',
          background: column.color,
          flexShrink: 0,
          borderRadius: '12px 12px 0 0',
        }}
      />

      {/* Column header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 16px 8px 16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span style={{ fontSize: '16px' }}>{column.icon}</span>
          <h3
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--dp-text-primary, #F5F5F5)',
              margin: 0,
              fontFamily: 'var(--dp-font)',
            }}
          >
            {column.title}
          </h3>
        </div>
        <span
          style={{
            fontSize: '12px',
            fontWeight: 600,
            padding: '3px 10px',
            borderRadius: '12px',
            backgroundColor: column.bgColor,
            color: column.color,
            fontFamily: 'var(--dp-font)',
          }}
        >
          {count}
        </span>
      </div>

      {/* Column description */}
      <p
        style={{
          fontSize: '11px',
          color: 'var(--dp-text-tertiary, #6B7280)',
          margin: '0 16px 10px 16px',
          fontFamily: 'var(--dp-font)',
        }}
      >
        {column.description}
      </p>

      {/* Droppable area */}
      <Droppable droppableId={column.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '4px 10px 10px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              minHeight: '80px',
              transition: 'background 0.2s ease',
              background: snapshot.isDraggingOver
                ? `${column.color}10`
                : 'transparent',
              borderRadius: snapshot.isDraggingOver ? '8px' : '0',
              border: snapshot.isDraggingOver
                ? `2px dashed ${column.color}40`
                : '2px solid transparent',
            }}
          >
            {isLoading ? (
              <>
                <KanbanCardSkeleton />
                <KanbanCardSkeleton />
                <KanbanCardSkeleton />
              </>
            ) : visibleVideos.length === 0 ? (
              <KanbanEmptyColumn column={column} />
            ) : (
              <>
                {visibleVideos.map((video, index) => (
                  <Draggable
                    key={video.id}
                    draggableId={video.id.toString()}
                    index={index}
                    isDragDisabled={video.is_imported}
                  >
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        style={getDragWrapperStyle(dragProvided.draggableProps.style, dragSnapshot)}
                      >
                        <KanbanCard
                          video={video}
                          isDragging={dragSnapshot.isDragging}
                          columnColor={column.color}
                          onChecklistChange={onChecklistChange}
                          onOpenScript={onOpenScript}
                          onCardClick={onCardClick}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}

                {/* Load more button (postado column) */}
                {hasMore && (
                  <button
                    onClick={handleLoadMore}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: `${column.color}15`,
                      border: `1px solid ${column.color}30`,
                      borderRadius: '8px',
                      color: column.color,
                      fontSize: '12px',
                      fontWeight: 500,
                      fontFamily: 'var(--dp-font)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `${column.color}25`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = `${column.color}15`;
                    }}
                  >
                    {t('kanban.see_more', { count: videos.length - visibleCount })}
                  </button>
                )}
              </>
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
});

KanbanColumn.displayName = 'KanbanColumn';

export default KanbanColumn;
